import * as XLSX from 'xlsx';
import {
    getAsRepairExcelFieldMap,
} from './asRepairExcelImport';
import {
    AS_REPAIR_MANAGEMENT_SCOPE,
    formatAsRepairAutoDisplay,
    formatAsRepairQuantityDisplay,
    getAsRepairFieldDisplayValue,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
    shouldAlwaysShowAsRepairDetailField,
} from './asRepairFormConfig';

/** A/S 수리 관리 — 수리내역 리스트 다운로드 기본 열 (A열부터 연속) */
const REPAIR_HISTORY_FIXED_COLUMNS = [
    { role: 'boxNumber', header: '박스번호' },
    { role: 'modelName', header: '모델명' },
    { role: 'siteName', header: '현장명' },
    { role: 'totalQty', header: '전체수량' },
    { role: 'shipDate', header: '출고일자' },
];

/** A/S 개인 관리 — 수리완료 리스트 다운로드(내부) */
export const PERSONAL_REPAIR_COMPLETE_INTERNAL_HEADERS = [
    '박스번호',
    '모델명',
    '품목명',
    '옵션사항',
    '현장명',
    '불량명',
    '출고일',
    '발송일자',
    '전체수량',
    '실입고수량',
    '수량부족',
    '미완료수량',
    '반품수량',
    '수리기간초과',
    '폐기제품',
    '외부압력',
    '패턴오픈',
    '수리완료수량',
    '유상수리수량',
    '정상수량',
    '사출스크레치',
    '플레이트스크레치',
    '이물질',
    'LCD파손',
    '다이얼파손',
    '온도센서파손',
    '커넥터파손',
    '케이블파손',
    'Micom파손',
    '미세먼지소음',
    '무상수리수량',
    '전원불량',
    'COIL간섭',
    'TRANS',
    'SMPS',
    '터치불량',
    '터치IC교체',
    '터치가스켓없음',
    '터치가스켓눌림',
    '소리불량',
    '스피커교체',
    '스피커냉땜',
    '홈넷불량',
    '홈넷IC교체',
    '홈넷TR교체',
    '보일러불량',
    '보일러IC교체',
    '보일러TR교체',
    '에어컨불량',
    '에어컨IC교체',
    '에어컨TR교체',
    '서브PCB불량',
    '서브PCB분실',
    '서브PCB펌웨어',
    'Micom교체',
    '먹통현상',
    '펌웨어업데이트',
    'LCD고장',
    '세그먼트불량',
    '냉땜',
    '릴레이고장',
    '작동정상',
    '리스트표기수정',
    '비고사항',
];

/** A/S 개인 관리 — 수리완료 리스트 다운로드(외부) */
export const PERSONAL_REPAIR_COMPLETE_EXTERNAL_HEADERS = [
    '박스번호',
    '모델명',
    '품목명',
    '옵션사항',
    '현장명',
    '불량명',
    '출고일',
    '발송일자',
    '전체수량',
    '실입고수량',
    '수량부족',
    '미완료수량',
    '반품수량',
    '수리기간초과',
    '폐기제품',
    '외부압력',
    '패턴오픈',
    '수리완료수량',
    '유상수리수량',
    '정상수량',
    '사출스크레치',
    '플레이트스크레치',
    '이물질',
    'LCD파손',
    '다이얼파손',
    '온도센서파손',
    '커넥터파손',
    '케이블파손',
    'Micom파손',
    '미세먼지소음',
    '리스트표기수정',
    '비고사항',
];

const HEADER_ALIASES = {
    출고일: ['출고일', '출고일자'],
    비고사항: ['비고사항', '비고', 'memo', 'Memo'],
    COIL간섭: ['COIL간섭', 'COIL 간섭', 'Coil간섭'],
    사출스크레치: ['사출스크레치', '스크레치'],
};

const normalizeHeaderToken = (value) => String(value || '')
    .replace(/\s+/g, '')
    .replace(/[·・]/g, '')
    .toLowerCase();

/** 리스트 세부사항과 동일한 필드 순서 */
export const getAsRepairDetailExportFields = (config, listSummary, excludeKeys = new Set()) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const summaryKeys = listSummary?.summaryKeys || new Set();
    return fields.filter((field) => {
        if (!field?.key || excludeKeys.has(field.key)) {
            return false;
        }
        return (
            !summaryKeys.has(field.key)
            || field.inputType === 'textarea'
            || field.inputType === 'text'
            || field.inputType === 'quantity'
            || shouldAlwaysShowAsRepairDetailField(field)
        );
    });
};

const isZeroExportValue = (raw) => {
    if (raw == null || raw === '') {
        return true;
    }
    if (raw === 0) {
        return true;
    }
    const text = String(raw).trim();
    if (text === '' || text === '-' || text === '0' || text === '0.0') {
        return true;
    }
    if (/^-?\d+(\.0+)?$/.test(text) && Number(text) === 0) {
        return true;
    }
    return false;
};

const cellValueForExport = (field, row, config, role = null) => {
    if (!field) {
        return '';
    }
    const raw = getAsRepairFieldDisplayValue(field, row, config);
    if (isZeroExportValue(raw)) {
        return '';
    }
    if (field.inputType === 'auto') {
        const text = formatAsRepairAutoDisplay(raw);
        if (isZeroExportValue(text)) {
            return '';
        }
        return text;
    }
    if (field.inputType === 'quantity' || role === 'totalQty') {
        const text = formatAsRepairQuantityDisplay(raw);
        return text === '' ? '' : parseQuantityValue(raw);
    }
    if (field.inputType === 'date' || role === 'shipDate') {
        return String(raw).slice(0, 10);
    }
    return String(raw);
};

const getHeaderAliasList = (header) => {
    const aliases = HEADER_ALIASES[header];
    return aliases && aliases.length > 0 ? aliases : [header];
};

const findFieldByHeader = (fields, header, usedKeys = new Set()) => {
    const pool = fields.filter((f) => f?.key && !usedKeys.has(f.key));
    const aliasTokens = getHeaderAliasList(header).map(normalizeHeaderToken);

    // 1) 라벨 완전 일치
    for (const token of aliasTokens) {
        const exact = pool.find((f) => normalizeHeaderToken(f.label) === token);
        if (exact) {
            return exact;
        }
    }
    // 2) 키 완전 일치 (custom_ 제거)
    for (const token of aliasTokens) {
        const byKey = pool.find((f) => {
            const keyToken = normalizeHeaderToken(String(f.key || '').replace(/^custom_/i, ''));
            return keyToken === token || normalizeHeaderToken(f.key) === token;
        });
        if (byKey) {
            return byKey;
        }
    }
    // 3) 포함 매칭 (짧은 라벨 오매칭 방지를 위해 긴 것 우선)
    const scored = [];
    pool.forEach((field) => {
        const labelToken = normalizeHeaderToken(field.label);
        const keyToken = normalizeHeaderToken(String(field.key || '').replace(/^custom_/i, ''));
        aliasTokens.forEach((token) => {
            if (!token) {
                return;
            }
            if (labelToken === token || keyToken === token) {
                scored.push({ field, score: 1000 + token.length });
                return;
            }
            if (labelToken.includes(token) || token.includes(labelToken)) {
                scored.push({ field, score: token.length });
            }
        });
    });
    if (scored.length === 0) {
        return null;
    }
    scored.sort((a, b) => b.score - a.score);
    return scored[0].field;
};

const buildOrderedHeaderExportRows = (rows, config, headers) => {
    const list = Array.isArray(rows) ? rows : [];
    const formConfig = normalizeAsRepairFormConfig(config);
    const fields = formConfig.fields;
    const usedKeys = new Set();
    const mappedFields = headers.map((header) => {
        const field = findFieldByHeader(fields, header, usedKeys);
        if (field?.key) {
            usedKeys.add(field.key);
        }
        return field;
    });

    const bodyRows = list.map((row) => (
        mappedFields.map((field) => cellValueForExport(field, row, formConfig))
    ));

    return {
        rows: [headers, ...bodyRows],
        rowCount: bodyRows.length,
    };
};

const buildSequentialDetailExportRows = (rows, config, listSummary, managementScope, fixedColumns) => {
    const list = Array.isArray(rows) ? rows : [];
    const formConfig = normalizeAsRepairFormConfig(config);
    const fieldMap = getAsRepairExcelFieldMap(formConfig, managementScope);
    const fixedFieldKeys = new Set(
        fixedColumns
            .map((col) => fieldMap[col.role]?.key)
            .filter(Boolean),
    );
    const detailFields = getAsRepairDetailExportFields(formConfig, listSummary, fixedFieldKeys);
    const headers = [
        ...fixedColumns.map((col) => col.header),
        ...detailFields.map((field) => field.label || field.key),
    ];

    const bodyRows = list.map((row) => {
        const fixedCells = fixedColumns.map((col) => (
            cellValueForExport(fieldMap[col.role], row, formConfig, col.role)
        ));
        const detailCells = detailFields.map((field) => (
            cellValueForExport(field, row, formConfig)
        ));
        return [...fixedCells, ...detailCells];
    });

    return {
        rows: [headers, ...bodyRows],
        rowCount: bodyRows.length,
    };
};

/** A/S 수리 관리 — 수리내역 리스트 (1행 헤더 + A열부터 연속) */
export const buildAsRepairRepairHistoryExcelRows = (rows, config, listSummary) => (
    buildSequentialDetailExportRows(
        rows,
        config,
        listSummary,
        AS_REPAIR_MANAGEMENT_SCOPE.REPAIR,
        REPAIR_HISTORY_FIXED_COLUMNS,
    )
);

/** A/S 개인 관리 — 수리완료 리스트(내부) */
export const buildAsRepairPersonalRepairCompleteInternalExcelRows = (rows, config) => (
    buildOrderedHeaderExportRows(rows, config, PERSONAL_REPAIR_COMPLETE_INTERNAL_HEADERS)
);

/** A/S 개인 관리 — 수리완료 리스트(외부) */
export const buildAsRepairPersonalRepairCompleteExternalExcelRows = (rows, config) => (
    buildOrderedHeaderExportRows(rows, config, PERSONAL_REPAIR_COMPLETE_EXTERNAL_HEADERS)
);

/** @deprecated 내부 다운로드로 대체 */
export const buildAsRepairPersonalRepairCompleteExcelRows = (rows, config) => (
    buildAsRepairPersonalRepairCompleteInternalExcelRows(rows, config)
);

const buildExportFileName = (
    managementScope,
    { detailListExport = false, personalExportMode = 'internal' } = {},
) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    let prefix = 'AS수리목록';
    if (managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL) {
        if (personalExportMode === 'external') {
            prefix = 'AS수리완료목록_외부';
        } else {
            prefix = 'AS수리완료목록_내부';
        }
    } else if (detailListExport) {
        prefix = 'AS수리내역목록';
    }
    return `${prefix}_${stamp}.xlsx`;
};

/** 선택한 A/S 목록을 XLSX로 저장 */
export const downloadAsRepairExcelFile = (
    rows,
    config,
    {
        fileName,
        managementScope,
        listSummary,
        detailListExport = false,
        personalExportMode = 'internal',
    } = {},
) => {
    const scope = managementScope || AS_REPAIR_MANAGEMENT_SCOPE.REPAIR;
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    let dataRows = [];
    let rowCount = 0;

    if (detailListExport && scope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL) {
        const built = personalExportMode === 'external'
            ? buildAsRepairPersonalRepairCompleteExternalExcelRows(list, config)
            : buildAsRepairPersonalRepairCompleteInternalExcelRows(list, config);
        dataRows = built.rows;
        rowCount = built.rowCount;
    } else if (detailListExport && scope === AS_REPAIR_MANAGEMENT_SCOPE.REPAIR) {
        const built = buildAsRepairRepairHistoryExcelRows(list, config, listSummary);
        dataRows = built.rows;
        rowCount = built.rowCount;
    } else {
        return { ok: false, reason: 'empty' };
    }

    if (dataRows.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    const sheet = XLSX.utils.aoa_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    XLSX.writeFile(
        workbook,
        fileName || buildExportFileName(scope, { detailListExport, personalExportMode }),
    );
    return { ok: true, rowCount };
};
